import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import { getVizTokens } from '../analytics/vizTokens';
import { EmptyState, StatTile } from '../analytics/components';
import StaffFormDialog from './StaffFormDialog';
import { ROLE_LABELS, money } from './shared';

function StaffList(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const navigate = useNavigate();

    const [staff, setStaff] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/staff/').then((res) => {
            setStaff(res.data.staff || []);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));

        // People waiting on a decision belong on the page you land on, not
        // behind another tap - an unanswered leave request is somebody's plans.
        api.get('/staff/requests/', { params: { status: 'pending' } })
            .then((res) => setPendingCount(res.data.pending_count || 0))
            .catch(() => setPendingCount(0));
    }, [showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const active = staff.filter((s) => s.is_active);
    const monthlyWageBill = active.reduce(
        (total, s) => total + Number(s.monthly_salary || 0), 0);
    const totalAdvances = staff.reduce(
        (total, s) => total + Number(s.outstanding_advances || 0), 0);

    return (
        <AccessController require="owner">
            <StaffFormDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSaved={() => { setAddOpen(false); load(); showSuccessMessage('Staff member added'); }}
                showErrorMessage={showErrorMessage}
            />

            <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
                    justifyContent: 'space-between', flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>Staff</Typography>
                        <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                            Salaries, advances, leave and payslips
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                        <Button variant="outlined" fullWidth={isMobile}
                            onClick={() => navigate('/staff/requests')}>
                            <Badge color="warning" badgeContent={pendingCount}
                                sx={{ '& .MuiBadge-badge': { right: -12, top: 2 } }}>
                                Approvals
                            </Badge>
                        </Button>
                        <Button variant="outlined" fullWidth={isMobile}
                            onClick={() => navigate('/staff/payroll')}>
                            Payroll
                        </Button>
                        <Button variant="contained" fullWidth={isMobile}
                            onClick={() => setAddOpen(true)}>
                            Add staff
                        </Button>
                    </Box>
                </Box>

                {pendingCount > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}
                        action={(
                            <Button size="small" color="inherit"
                                onClick={() => navigate('/staff/requests')}>
                                Review
                            </Button>
                        )}>
                        {pendingCount} request{pendingCount === 1 ? '' : 's'} waiting
                        on you
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : staff.length === 0 ? (
                    <Paper variant="outlined">
                        <EmptyState title="No staff yet" tokens={tokens}>
                            Add your team to track salaries, advances and leave, and to
                            generate payslips each month.
                        </EmptyState>
                    </Paper>
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6, md: 4 }}>
                                <StatTile tokens={tokens} label="On the team"
                                    value={active.length}
                                    sub={`${staff.length - active.length} former`} />
                            </Grid>
                            {/* Payroll figures are shown in full, never abbreviated:
                                "₹6.0K" is not an acceptable way to state what is
                                owed to or by a person. */}
                            <Grid size={{ xs: 6, md: 4 }}>
                                <StatTile tokens={tokens} label="Monthly wage bill"
                                    value={money(monthlyWageBill)}
                                    sub="active staff at full salary" />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatTile tokens={tokens} label="Advances outstanding"
                                    value={money(totalAdvances)}
                                    sub="not yet recovered"
                                    tone={totalAdvances > 0 ? 'critical' : undefined} />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1.5}>
                            {staff.map((member) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={member.id}>
                                    <Paper
                                        variant="outlined"
                                        onClick={() => navigate(`/staff/${member.id}`)}
                                        sx={{
                                            p: 2, height: '100%', cursor: 'pointer',
                                            opacity: member.is_active ? 1 : 0.6,
                                            '&:hover': { backgroundColor: 'action.hover' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'flex-start', gap: 1 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1"
                                                    sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                    {member.name}
                                                </Typography>
                                                <Typography variant="caption"
                                                    sx={{ color: tokens.textSecondary }}>
                                                    {ROLE_LABELS[member.role] || member.role}
                                                </Typography>
                                            </Box>
                                            {!member.is_active && (
                                                <Chip size="small" label="Former" variant="outlined" />
                                            )}
                                        </Box>
                                        <Typography sx={{ mt: 1, fontSize: '1.3rem', fontWeight: 600 }}>
                                            {money(member.monthly_salary)}
                                            <Typography component="span" variant="caption"
                                                sx={{ color: tokens.textMuted, ml: 0.5 }}>
                                                / month
                                            </Typography>
                                        </Typography>
                                        {Number(member.outstanding_advances) > 0 && (
                                            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                                                {money(member.outstanding_advances)} advance outstanding
                                            </Alert>
                                        )}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </Box>
        </AccessController>
    );
}

export default StaffList;
